import { Platform } from 'react-native';
import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type { AttendanceCapture } from '../types/faceAttendance';
import {
  VISIT_STATUS,
  type HrContact,
  type QuickVisitorInput,
  type QuickVisitorResult,
  type Visit,
  type VisitHistoryFilters,
  type Visitor,
  type VisitorFaceImage,
  type VisitorFaceResult,
  type VisitorPage,
  type VisitorRecognition,
  type VisitorRegistrationInput,
  type VisitStatus,
} from '../types/visitor';
import { browserAttendanceImage } from '../utils/browserAttendanceImage';
import { calendarDateFromApi } from '../utils/dateTime';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;
type PageData = {
  items?: ObjectValue[];
  pagination?: {
    page?: unknown;
    pageSize?: unknown;
    totalRecords?: unknown;
    totalPages?: unknown;
  };
};

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function normalizeVisitor(row: ObjectValue): Visitor | null {
  const visitorId = numberOrNull(row.visitorId);
  const visitorName = textOrNull(row.visitorName);
  if (!visitorId || !visitorName) return null;
  return {
    visitorId,
    visitorName,
    designation: textOrNull(row.designation),
    companyName: textOrNull(row.companyName) ?? '',
    phoneNumber: textOrNull(row.phoneNumber) ?? '',
    email: textOrNull(row.email),
    purposeOfVisit: textOrNull(row.purposeOfVisit) ?? '',
    isInside: row.isInside === true,
    lastCheckInAt: textOrNull(row.lastCheckInAt),
    createdAt: textOrNull(row.createdAt),
  };
}

function normalizeStatus(value: unknown): VisitStatus {
  return String(value).toUpperCase() === VISIT_STATUS.CHECKED_OUT
    ? VISIT_STATUS.CHECKED_OUT
    : VISIT_STATUS.INSIDE;
}

export function normalizeVisit(row: ObjectValue): Visit | null {
  const visitId = numberOrNull(row.visitId);
  const visitorId = numberOrNull(row.visitorId);
  const visitorName = textOrNull(row.visitorName);
  if (!visitId || !visitorId || !visitorName) return null;
  return {
    visitId,
    visitorId,
    visitorName,
    designation: textOrNull(row.designation),
    companyName: textOrNull(row.companyName) ?? '',
    purposeOfVisit: textOrNull(row.purposeOfVisit),
    visitDate: calendarDateFromApi(row.visitDate),
    visitStatus: normalizeStatus(row.visitStatus),
    checkInAt: textOrNull(row.checkInAt),
    checkOutAt: textOrNull(row.checkOutAt),
  };
}

function toPage<T>(
  data: PageData,
  normalize: (row: ObjectValue) => T | null,
  page: number,
  pageSize: number,
): VisitorPage<T> {
  const pagination = data.pagination ?? {};
  const items = (data.items ?? [])
    .map(normalize)
    .filter((item): item is T => item != null);
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages:
      numberOrNull(pagination.totalPages) ??
      Math.ceil(totalItems / resolvedPageSize),
  };
}

type FaceImageFile = Pick<VisitorFaceImage, 'uri' | 'fileName' | 'mimeType'>;

async function appendFaceImage(form: FormData, image: FaceImageFile) {
  if (Platform.OS === 'web') {
    const captured = await browserAttendanceImage(
      image as unknown as Pick<AttendanceCapture, 'uri' | 'fileName' | 'mimeType'>,
    );
    captured.appendTo(form);
    return;
  }
  form.append('image', {
    uri: image.uri,
    name: image.fileName,
    type: image.mimeType,
  } as unknown as Blob);
}

export async function listVisitors(input: {
  page: number;
  pageSize: number;
  search: string;
}): Promise<VisitorPage<Visitor>> {
  const search = input.search.trim();
  const response = await apiClient.get<Envelope<PageData>>(
    API_ENDPOINTS.VISITORS.LIST,
    {
      params: {
        page: input.page,
        pageSize: input.pageSize,
        ...(search ? { search } : {}),
      },
    },
  );
  return toPage(response.data.data ?? {}, normalizeVisitor, input.page, input.pageSize);
}

export async function listVisits(input: {
  page: number;
  pageSize: number;
  search: string;
  filters: VisitHistoryFilters;
}): Promise<VisitorPage<Visit>> {
  const search = input.search.trim();
  const date = input.filters.visitDate?.trim();
  const response = await apiClient.get<Envelope<PageData>>(
    API_ENDPOINTS.VISITORS.VISITS,
    {
      params: {
        page: input.page,
        pageSize: input.pageSize,
        ...(search ? { search } : {}),
        ...(date ? { fromDate: date, toDate: date } : {}),
        ...(input.filters.status ? { status: input.filters.status } : {}),
      },
    },
  );
  return toPage(response.data.data ?? {}, normalizeVisit, input.page, input.pageSize);
}

export async function registerVisitor(
  input: VisitorRegistrationInput,
): Promise<Visitor> {
  const form = new FormData();
  await appendFaceImage(form, input.faceImage);
  form.append('visitorName', input.visitorName.trim());
  form.append('companyName', input.companyName.trim());
  form.append('phoneNumber', input.phoneNumber.trim());
  form.append('purposeOfVisit', input.purposeOfVisit.trim());
  form.append('consentGiven', input.consentGiven ? 'true' : 'false');
  if (input.designation.trim()) form.append('designation', input.designation.trim());
  if (input.email.trim()) form.append('email', input.email.trim());

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.VISITORS.REGISTER,
    form,
    MULTIPART,
  );
  const visitor = normalizeVisitor(response.data.data ?? {});
  if (!visitor) {
    throw Object.assign(new Error('The visitor could not be registered.'), {
      code: 'VISITOR_REGISTRATION_FAILED',
    });
  }
  return visitor;
}

/**
 * With `faceImage` (the capture from Mark Attendance) the visitor's face is
 * enrolled and the visitor checked in; without it only the form is saved.
 */
export async function quickRegisterVisitor(
  input: QuickVisitorInput,
  faceImage?: FaceImageFile | null,
): Promise<QuickVisitorResult> {
  const body: Record<string, string> = {
    visitorName: input.visitorName.trim(),
    phoneNumber: input.phoneNumber.trim(),
  };
  for (const key of ['designation', 'companyName', 'email', 'purposeOfVisit'] as const) {
    const value = input[key].trim();
    if (value) body[key] = value;
  }
  let form: FormData | null = null;
  if (faceImage) {
    form = new FormData();
    await appendFaceImage(form, faceImage);
    for (const [key, value] of Object.entries(body)) form.append(key, value);
  }
  const response = form
    ? await apiClient.post<Envelope<ObjectValue>>(
        API_ENDPOINTS.VISITORS.QUICK_REGISTER,
        form,
        MULTIPART,
      )
    : await apiClient.post<Envelope<ObjectValue>>(
        API_ENDPOINTS.VISITORS.QUICK_REGISTER,
        body,
      );
  const data = response.data.data ?? {};
  const visitor = normalizeVisitor((data.visitor as ObjectValue) ?? {});
  if (!visitor) {
    throw Object.assign(new Error('The visitor could not be registered.'), {
      code: 'VISITOR_REGISTRATION_FAILED',
    });
  }
  const visit = data.visit ? normalizeVisit(data.visit as ObjectValue) : null;
  return {
    visitor,
    organizationName: textOrNull(data.organizationName),
    welcomeMessage:
      textOrNull(data.welcomeMessage) ?? `Welcome, ${visitor.visitorName}!`,
    faceRegistered: data.faceRegistered === true,
    checkedIn: data.checkedIn === true && visit != null,
    visit,
  };
}

/**
 * Looks for a registered visitor with the face captured at Mark Attendance:
 * their open visit is checked out, otherwise they are checked in. A face that
 * belongs to no visitor rejects with VISITOR_FACE_NOT_MATCHED.
 */
export async function recognizeVisitor(
  image: FaceImageFile,
): Promise<VisitorRecognition> {
  const form = new FormData();
  await appendFaceImage(form, image);
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.VISITORS.RECOGNIZE,
    form,
    MULTIPART,
  );
  const data = response.data.data ?? {};
  const visit = normalizeVisit((data.visit as ObjectValue) ?? {});
  if (!visit) {
    throw Object.assign(new Error('The visit could not be recorded.'), {
      code: 'VISIT_RESULT_INVALID',
    });
  }
  const action = data.action === 'CHECK_IN' ? 'CHECK_IN' : 'CHECK_OUT';
  return {
    action,
    visit,
    organizationName: textOrNull(data.organizationName),
    message:
      textOrNull(data.message) ??
      (action === 'CHECK_OUT'
        ? `Thank you for visiting, ${visit.visitorName}! We hope to see you again.`
        : `Welcome, ${visit.visitorName}!`),
  };
}

export async function getHrContact(): Promise<HrContact> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.VISITORS.HR_CONTACT,
  );
  const data = response.data.data ?? {};
  return {
    organizationName: textOrNull(data.organizationName),
    contactName: textOrNull(data.contactName),
    phoneNumber: textOrNull(data.phoneNumber),
    email: textOrNull(data.email),
  };
}

async function submitFace(
  endpoint: string,
  image: VisitorFaceImage,
): Promise<VisitorFaceResult> {
  const form = new FormData();
  await appendFaceImage(form, image);
  const response = await apiClient.post<Envelope<ObjectValue>>(
    endpoint,
    form,
    MULTIPART,
  );
  const data = response.data.data ?? {};
  const visit = normalizeVisit((data.visit as ObjectValue) ?? {});
  if (!visit) {
    throw Object.assign(new Error('The visit could not be recorded.'), {
      code: 'VISIT_RESULT_INVALID',
    });
  }
  return {
    visit,
    message: textOrNull(data.message) ?? textOrNull(response.data.message) ?? '',
  };
}

export function checkInVisitor(image: VisitorFaceImage) {
  return submitFace(API_ENDPOINTS.VISITORS.CHECK_IN, image);
}

export function checkOutVisitor(image: VisitorFaceImage) {
  return submitFace(API_ENDPOINTS.VISITORS.CHECK_OUT, image);
}

export async function deleteVisitor(visitorId: number): Promise<void> {
  await apiClient.delete(API_ENDPOINTS.VISITORS.DELETE(visitorId));
}
